import type { Command } from '../../types/commands'
import { formatCommandOutput, colorize, formatHighlight, formatSuccess } from '../colorScheme'
import { dividers } from '../asciiArt'

export const experienceCommand: Command = {
  name: 'experience',
  description: 'Show work experience',
  aliases: ['work', 'jobs', 'career'],
  category: 'info',
  execute: () => {
    const expText = `
╔═══════════════════════════════════════════════════════════╗
║              ${formatHighlight('PROFESSIONAL EXPERIENCE')}                       ║
╚═══════════════════════════════════════════════════════════╝

┌─ 📁 ${formatHighlight('ls -la /experience/')} ───────────────────────────────────┐
│                                                            │
│  ┌───────────────────────────────────────────────────────┐│
│  │ ${colorize('2024 - present', 'brightBlack')} │ ${colorize('can.code_labs/', 'brightBlue')}                        ││
│  └───────────────────────────────────────────────────────┘│
│    ${colorize('▸', 'cyan')} Founder & Lead Engineer                              │
│    ${colorize('▸', 'cyan')} ${colorize('80+', 'magenta')} projects: Next.js, Go CLIs, React, Ruby tools    │
│    ${colorize('▸', 'cyan')} Voice-AI ecosystem, agent memory, streaming arch     │
│    ${colorize('▸', 'cyan')} Production Go CLI at ${colorize('13K+', 'magenta')} lines                       │
│                                                            │
│  ┌───────────────────────────────────────────────────────┐│
│  │ ${colorize('2022 - 2024', 'brightBlack')}    │ ${colorize('huntress/', 'brightBlue')}                            ││
│  └───────────────────────────────────────────────────────┘│
│    ${colorize('▸', 'cyan')} Software Engineer                                    │
│    ${colorize('▸', 'cyan')} Microsoft 365 enterprise security monitoring         │
│    ${colorize('▸', 'cyan')} ${colorize('99.9%+', 'magenta')} uptime for security-critical systems         │
│    ${colorize('▸', 'cyan')} SRE rotation, incident response, root cause analysis │
│                                                            │
│  ┌───────────────────────────────────────────────────────┐│
│  │ ${colorize('2019 - 2022', 'brightBlack')}    │ ${colorize('taxjar/', 'brightBlue')} ${colorize('(acquired by Stripe)', 'brightBlack')}          ││
│  └───────────────────────────────────────────────────────┘│
│    ${colorize('▸', 'cyan')} Software Engineer                                    │
│    ${colorize('▸', 'cyan')} Automated audit requests: ${colorize('2+ hrs', 'magenta')} to ${colorize('10-15 min', 'magenta')}          │
│    ${colorize('▸', 'cyan')} Eliminated ${colorize('100+', 'magenta')} dev hours/year, zero false positives │
│    ${colorize('▸', 'cyan')} ${colorize('50K+', 'magenta')} users, billions in annual transactions          │
│    ${colorize('  ', 'cyan')} ${colorize('>> Type story taxjar for the full case study', 'brightBlack')}      │
│                                                            │
│  ┌───────────────────────────────────────────────────────┐│
│  │ ${colorize('2016 - 2019', 'brightBlack')}    │ ${colorize('paymentspring/', 'brightBlue')} / ${colorize('cardflight/', 'brightBlue')}             ││
│  └───────────────────────────────────────────────────────┘│
│    ${colorize('▸', 'cyan')} Software Engineer                                    │
│    ${colorize('▸', 'cyan')} Core payment processing libraries (mobile SDK)       │
│    ${colorize('▸', 'cyan')} AWS SAM, Lambda, Step Functions, CloudFormation      │
│    ${colorize('▸', 'cyan')} Processor certs: Vantiv/Worldpay, First Data         │
│                                                            │
│  ┌───────────────────────────────────────────────────────┐│
│  │ ${colorize('2007 - 2015', 'brightBlack')}    │ ${colorize('prairie_hill_learning_center/', 'brightBlue')}        ││
│  └───────────────────────────────────────────────────────┘│
│    ${colorize('▸', 'cyan')} Lead Teacher, Farm-based Montessori                  │
│    ${colorize('▸', 'cyan')} Origin of engineering approach: curiosity-driven      │
│    ${colorize('▸', 'cyan')} Meeting people where they are                        │
└────────────────────────────────────────────────────────────┘

${colorize(dividers.circuit, 'brightBlack')}

┌─ 📁 ${formatHighlight('cat /experience/achievements.txt')} ──────────────────────┐
│                                                            │
│  🎯 ${formatHighlight('KEY ACHIEVEMENTS')}                                       │
│  ─────────────────────────────────────────────────────────│
│    ${formatSuccess('✓')} Built ${colorize('80+', 'magenta')} projects in two years at can.code        │
│    ${formatSuccess('✓')} Automated TaxJar audit: saved ${colorize('100+', 'magenta')} dev hrs/year    │
│    ${formatSuccess('✓')} ${colorize('99.9%+', 'magenta')} uptime at Huntress security systems        │
│    ${formatSuccess('✓')} Production Go CLI, ${colorize('90+', 'magenta')} React components            │
│    ${formatSuccess('✓')} Voice-AI ecosystem with agent memory                │
└────────────────────────────────────────────────────────────┘

${colorize(dividers.stars, 'yellow')}

┌─ 📁 ${formatHighlight('cat /experience/expertise.txt')} ─────────────────────────┐
│                                                            │
│  💼 ${formatHighlight('CORE COMPETENCIES')}                                      │
│  ─────────────────────────────────────────────────────────│
│    ${colorize('▸', 'cyan')} ${colorize('10+', 'magenta')} years Ruby on Rails development                 │
│    ${colorize('▸', 'cyan')} AI/LLM development and implementation                │
│    ${colorize('▸', 'cyan')} Payment processing & PCI compliance                  │
│    ${colorize('▸', 'cyan')} Tax automation at enterprise scale                   │
│    ${colorize('▸', 'cyan')} Enterprise security monitoring (M365)                │
│    ${colorize('▸', 'cyan')} Voice-AI and assistive technology                    │
│    ${colorize('▸', 'cyan')} Accessibility-first architecture (WCAG 2.1 AA/AAA)   │
└────────────────────────────────────────────────────────────┘
`

    // Apply automatic formatting for technology names
    const formattedText = formatCommandOutput(expText, {
      highlightTech: true,
      highlightNumbers: true,
      highlightDates: false,
      highlightPaths: false,
      highlightUrls: false,
    })

    return [
      {
        type: 'info',
        content: (
          <div dangerouslySetInnerHTML={{ __html: formattedText }} />
        ),
      },
    ]
  },
}
